import * as _ from 'lodash';
import {
  useDataTransforms,
  useFarmName,
  useFilterYears,
  useStartYear,
  useYearsArray,
} from '../utils/hooks';
import ItemsTable from '../components/tables/ItemsTable';
import MetricsSummary from '../components/economics/MetricsSummary';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import { ANNUAL_MULTI_YEAR, MULTI_YEAR, N_YEARS } from '../constants/years';
import Content from '../components/layout/Content';
import { useFilterContext } from '../contexts/FilterContext';
import { useSubsidySwitchContext } from '../contexts/SubsidySwitchContext';
import useOpCategoryTabs from '../hooks/useOpCategoryTabs';
import { getOperationItems, toOperationsTableData } from '../utils/operations';
import { getMetricsForYear, getYearlyMetrics } from '../utils/columns';
import { asYearString } from '../utils/formatters';

/*
This is a template used for OperationsView and LaborView, which are currently
very similar. If they diverge significantly, consider splitting this component back
into their respective views.
*/
const OperationsViewTemplate = ({
  msg,
  data,
  toSummaryData,
  getSummaryMetrics,
  metricsForCategory,
  header,
  filterForYear,
}) => {
  const filterYears = useFilterYears();
  const { shouldShowSubsidies } = useSubsidySwitchContext();
  const {
    state: { year: filterYear, years },
  } = useFilterContext();
  const farmName = useFarmName();
  const farmStartYear = useStartYear();

  const allYears = useYearsArray(N_YEARS);

  const { activeCategory, opCategoryTabs } = useOpCategoryTabs();

  const [
    summaryData = {},
    summaryMetrics = [],
    tableData = {},
  ] = useDataTransforms(data, [
    toSummaryData,
    getSummaryMetrics,
    toOperationsTableData,
  ]);

  const cols = [
    ...(filterYear === ANNUAL_MULTI_YEAR
      ? [
          {
            displayString: 'Year',
            accessor: 'year',
          },
        ]
      : []),
    ...metricsForCategory(activeCategory, filterYear),
  ];

  const metricsForYear = getMetricsForYear(cols);

  const yearlyMetrics = getYearlyMetrics(cols);

  const getItems = getOperationItems(
    activeCategory,
    filterYear,
    years,
    filterForYear,
    yearlyMetrics,
    farmStartYear,
  );

  return (
    <>
      <MetricsHeader
        showCumulativeYearSelect
        showAnnualMultiYearSelect
        showAnnualSingleYearSelect
      >
        {header}
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={summaryMetrics}
            data={summaryData}
            arrayTransform={filterYears}
            msg={msg}
          />
        </SummarySection>
        <DetailsSection>
          <ItemsTable
            data={tableData}
            cols={filterYear === MULTI_YEAR ? cols : metricsForYear}
            msg={msg}
            getItems={getItems}
            byCrop
            csvFileName={[
              header,
              _.capitalize(activeCategory ?? 'All'),
              ...([asYearString(filterYear, years, allYears)] ?? []),
              ...(shouldShowSubsidies ? ['subsidies applied'] : []),
              farmName,
            ].join(' - ')}
            tableHeader={opCategoryTabs}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default OperationsViewTemplate;
